import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { useBookmarks } from '../contexts/BookmarkContext';
import { useTranslation } from 'react-i18next';
import { JobPosting, JobPostingSearchRequest } from '../types';
import { apiService } from '../services/api';
import { Loading, BookmarkButton } from '../components';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Search, FileText, DollarSign, MapPin, Clock, User, SlidersHorizontal, X, ArrowUpDown } from 'lucide-react';
import { useDataDictionary, DATA_DICTIONARY_TYPES } from '../hooks/useDataDictionary';
import { formatDate } from '../utils/date';
import { getDictValueByCode, getDictDisplayValue } from '../utils/i18n';

const JobPostingListPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const { showAlert } = useAlert();
  const { toggleJobPostBookmark, isJobPostBookmarked, error, clearError } = useBookmarks();
  const [jobPostings, setJobPostings] = useState<JobPosting[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState(() => searchParams.get('keyword') || '');
  const [filterLocation, setFilterLocation] = useState<string>('');
  const [filterMinSalary, setFilterMinSalary] = useState<string>('');
  const [filterMaxSalary, setFilterMaxSalary] = useState<string>('');
  const [filterRequiredSkills, setFilterRequiredSkills] = useState<string[]>([]);
  const [filterRequiredLanguages, setFilterRequiredLanguages] = useState<string[]>([]);
  const [filterMinExperience, setFilterMinExperience] = useState<string>('');
  const [totalElements, setTotalElements] = useState<number>(0);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC');

  const { dictionaries: jobLocationDicts } = useDataDictionary({
    parentCode: DATA_DICTIONARY_TYPES.JOB_LOCATION,
  });
  const { dictionaries: careDicts } = useDataDictionary({ parentCode: DATA_DICTIONARY_TYPES.RESPONSIBILITIES_CARE });
  const { dictionaries: houseworkDicts } = useDataDictionary({ parentCode: DATA_DICTIONARY_TYPES.HOUSEWORK });
  const { dictionaries: languageDicts } = useDataDictionary({ parentCode: DATA_DICTIONARY_TYPES.LANGUAGE_SKILLS });

  useEffect(() => {
    if (error) {
      showAlert({ type: 'error', message: error });
      clearError();
    }
  }, [error, clearError, showAlert]);

  const appliedKeyword = useRef('');

  const loadJobPostings = useCallback(async (pageNum: number = 0) => {
    if (authLoading) return;
    try {
      setLoading(true);
      const searchRequest: JobPostingSearchRequest = {
        page: pageNum,
        size: 15,
        sortBy,
        sortDirection,
        status: 'ACTIVE',
      };
      if (appliedKeyword.current.trim()) searchRequest.keyword = appliedKeyword.current.trim();
      if (filterLocation && filterLocation !== 'all') searchRequest.location = filterLocation;
      if (filterMinSalary) searchRequest.minSalary = Number(filterMinSalary);
      if (filterMaxSalary) searchRequest.maxSalary = Number(filterMaxSalary);
      if (filterRequiredSkills.length > 0) searchRequest.requiredSkills = filterRequiredSkills.join(',');
      if (filterRequiredLanguages.length > 0) searchRequest.requiredLanguages = filterRequiredLanguages.join(',');
      if (filterMinExperience) searchRequest.minExperienceYears = Number(filterMinExperience);

      const response = await apiService.jobPostings.search(searchRequest);
      if (response.success && response.data) {
        const { content, totalPages: tp, totalElements: te } = response.data;
        setJobPostings(content);
        setHasMore(pageNum < tp - 1);
        setTotalPages(tp);
        setPage(pageNum);
        if (te !== undefined) setTotalElements(te);
      }
    } catch (error: any) {
      console.error('Error loading job postings:', error);
      showAlert({ type: 'error', message: t('jobPostingList.loadFailed') });
    } finally {
      setLoading(false);
    }
  }, [authLoading, filterLocation, filterMinSalary, filterMaxSalary, filterRequiredSkills, filterRequiredLanguages, filterMinExperience, sortBy, sortDirection, showAlert, t]);

  useEffect(() => {
    const kw = searchParams.get('keyword');
    if (kw) appliedKeyword.current = kw;
    loadJobPostings(0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    const timer = setTimeout(() => loadJobPostings(0), 400);
    return () => clearTimeout(timer);
  }, [loadJobPostings]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePrevPage = () => {
    if (!loading && page > 0) loadJobPostings(page - 1);
  };

  const handleNextPage = () => {
    if (!loading && hasMore) loadJobPostings(page + 1);
  };

  const handleResetFilters = () => {
    setFilterLocation('');
    setFilterMinSalary('');
    setFilterMaxSalary('');
    setFilterRequiredSkills([]);
    setFilterRequiredLanguages([]);
    setFilterMinExperience('');
    setIsMobileFilterOpen(false);
  };

  const activeFilterCount = [
    filterLocation !== '',
    filterMinSalary !== '',
    filterMaxSalary !== '',
    filterRequiredSkills.length > 0,
    filterRequiredLanguages.length > 0,
    filterMinExperience !== '',
  ].filter(Boolean).length;

  const formatSalary = (posting: JobPosting) => {
    if (posting.salaryType === 'FIXED' && posting.fixedSalary) {
      const currency = posting.salaryCurrency === 'HKD' ? 'HK$' : '$';
      return `${currency}${posting.fixedSalary.toLocaleString()}`;
    }
    if (posting.salaryType === 'RANGE' && posting.minSalary && posting.maxSalary) {
      const currency = posting.salaryCurrency === 'HKD' ? 'HK$' : '$';
      return `${currency}${posting.minSalary.toLocaleString()} - ${currency}${posting.maxSalary.toLocaleString()}`;
    }
    if (posting.salaryType === 'OTHER') {
      if (posting.salaryOtherOption === 'BASED_ON_EXPERIENCE') return t('jobPosting.salaryBasedOnExperience', '根據經驗');
      if (posting.salaryOtherOption === 'LEGAL_MINIMUM') return t('jobPosting.salaryLegalMinimum', '法定最低工資');
      if (posting.salaryOtherOption === 'TO_BE_DISCUSS') return t('jobPosting.salaryToBeDiscussed', '面議');
    }
    return t('common.notSpecified', '不指定');
  };

  const formatDateLocal = (dateString?: string) => {
    return formatDate(dateString) || t('common.notSpecified', '未指定');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-100 text-green-800 border-green-200';
      case 'CLOSED': return 'bg-destructive/10 text-destructive border-destructive/20';
      case 'DRAFT': return 'bg-muted text-foreground border-border';
      default: return 'bg-muted text-foreground border-border';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'ACTIVE': return t('jobPosting.statusActive', '活躍');
      case 'CLOSED': return t('jobPosting.statusClosed', '已關閉');
      case 'DRAFT': return t('jobPosting.statusDraft', '草稿');
      default: return status;
    }
  };

  const filteredPostings = jobPostings;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-[#fee39e]/30 py-12 md:py-16">
          <div className="container mx-auto px-4">
            <h1 className="text-3xl md:text-4xl font-bold text-secondary-foreground mb-4 text-center">
              {t('jobPostingList.title', '尋找工作機會')}
            </h1>
            <p className="text-secondary-foreground/80 text-center mb-8 max-w-2xl mx-auto">
              {t('jobPostingList.subtitle', '瀏覽香港僱主發布的工作機會，找到最適合您的職位')}
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder={t('jobPostingList.searchPlaceholder', '搜尋職位標題、描述或地點...')}
                  className="pl-12 h-12 bg-background text-foreground"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { appliedKeyword.current = searchKeyword; loadJobPostings(0); } }}
                />
              </div>
              <Button className="h-12 px-6 shrink-0" onClick={() => { appliedKeyword.current = searchKeyword; loadJobPostings(0); }}>
                {t('common.search', '搜尋')}
              </Button>
            </div>
          </div>
        </section>

        {/* Filters & Results */}
        <section className="py-8 bg-muted/50 min-h-[500px]">
          <div className="container mx-auto px-4">
            <div className="flex flex-col lg:flex-row gap-8">

              {/* Left Sidebar - Filters */}
              <aside className="hidden lg:block w-72 flex-shrink-0">
                  <div className="sticky top-24 bg-card rounded-xl border p-6 space-y-6">
                    <div className="flex items-center justify-between">
                      <h2 className="font-semibold text-lg">{t('jobPostings.filters.title', '篩選條件')}</h2>
                      {activeFilterCount > 0 && (
                        <Button variant="ghost" size="sm" onClick={handleResetFilters} className="h-8 px-2 text-xs">
                          {t('jobPostings.filters.clearFilters', '清除篩選')}
                        </Button>
                      )}
                    </div>

                    {/* Location Filter */}
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">{t('jobPostings.filters.location', '工作地點')}</Label>
                      <Select value={filterLocation || 'all'} onValueChange={(val) => setFilterLocation(val === 'all' ? '' : val)}>
                        <SelectTrigger className="w-full bg-background">
                          <SelectValue placeholder={t('candidates.anyLocation', '任何地點')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('candidates.anyLocation', '任何地點')}</SelectItem>
                          {jobLocationDicts.map(dict => (
                            <SelectItem key={dict.code} value={dict.valueEnglish}>{getDictDisplayValue(dict)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Salary Range */}
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">{t('jobPostings.filters.salaryRange', '薪金範圍')}</Label>
                      <div className="flex gap-2">
                        <Input type="number" placeholder={t('candidates.minSalary', '最低')} value={filterMinSalary} onChange={(e) => setFilterMinSalary(e.target.value)} className="bg-background" min="0" />
                        <Input type="number" placeholder={t('candidates.maxSalary', '最高')} value={filterMaxSalary} onChange={(e) => setFilterMaxSalary(e.target.value)} className="bg-background" min="0" />
                      </div>
                    </div>

                    {/* Min Experience */}
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">{t('jobPostings.filters.minExperience', '最少經驗 (年)')}</Label>
                      <Input type="number" placeholder="0" value={filterMinExperience} onChange={(e) => setFilterMinExperience(e.target.value)} className="bg-background" min="0" />
                    </div>

                    {/* Required Languages */}
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">{t('jobPostings.filters.requiredLanguages', '語言能力')}</Label>
                      <div className="grid grid-cols-2 gap-x-2 gap-y-2">
                        {languageDicts.map(dict => (
                          <div key={dict.code} className="flex items-center gap-2">
                            <Checkbox
                              id={`lang-${dict.code}`}
                              checked={filterRequiredLanguages.includes(dict.code)}
                              onCheckedChange={(checked) => {
                                if (checked) setFilterRequiredLanguages([...filterRequiredLanguages, dict.code]);
                                else setFilterRequiredLanguages(filterRequiredLanguages.filter(c => c !== dict.code));
                              }}
                            />
                            <label htmlFor={`lang-${dict.code}`} className="text-sm cursor-pointer">{getDictDisplayValue(dict)}</label>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Required Skills */}
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">{t('jobPostings.filters.requiredSkills', '技能')}</Label>
                      <div className="grid grid-cols-2 gap-x-2 gap-y-2">
                        {[...careDicts, ...houseworkDicts].map(dict => (
                          <div key={dict.code} className="flex items-center gap-2">
                            <Checkbox
                              id={`skill-${dict.code}`}
                              checked={filterRequiredSkills.includes(dict.code)}
                              onCheckedChange={(checked) => {
                                if (checked) setFilterRequiredSkills([...filterRequiredSkills, dict.code]);
                                else setFilterRequiredSkills(filterRequiredSkills.filter(c => c !== dict.code));
                              }}
                            />
                            <label htmlFor={`skill-${dict.code}`} className="text-sm cursor-pointer">{getDictDisplayValue(dict)}</label>
                          </div>
                        ))}
                      </div>
                    </div>

                  </div>
                </aside>

              {/* Main Content */}
              <div className="flex-1 min-w-0">
                {/* Mobile Filter Button */}
                <div className="lg:hidden mb-4">
                    <Button
                      variant="outline"
                      className="w-full gap-2"
                      onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
                    >
                      <SlidersHorizontal className="h-4 w-4" />
                      {t('jobPostings.filters.title', '篩選條件')}
                      {activeFilterCount > 0 && (
                        <Badge variant="secondary" className="ml-1">{activeFilterCount}</Badge>
                      )}
                    </Button>
                  </div>

                {/* Mobile Filters (collapsible) */}
                {isMobileFilterOpen && (
                  <div className="lg:hidden mb-6 bg-card rounded-xl border p-4 space-y-4">
                    {/* Location */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">{t('jobPostings.filters.location', '工作地點')}</Label>
                      <Select value={filterLocation || 'all'} onValueChange={(val) => setFilterLocation(val === 'all' ? '' : val)}>
                        <SelectTrigger className="w-full bg-background"><SelectValue placeholder={t('candidates.anyLocation', '任何地點')} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('candidates.anyLocation', '任何地點')}</SelectItem>
                          {jobLocationDicts.map(dict => (<SelectItem key={dict.code} value={dict.valueEnglish}>{getDictDisplayValue(dict)}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    {/* Salary Range */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">{t('jobPostings.filters.salaryRange', '薪金範圍')}</Label>
                      <div className="flex gap-2">
                        <Input type="number" placeholder={t('candidates.minSalary', '最低')} value={filterMinSalary} onChange={(e) => setFilterMinSalary(e.target.value)} className="bg-background" min="0" />
                        <Input type="number" placeholder={t('candidates.maxSalary', '最高')} value={filterMaxSalary} onChange={(e) => setFilterMaxSalary(e.target.value)} className="bg-background" min="0" />
                      </div>
                    </div>
                    {/* Min Experience */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">{t('jobPostings.filters.minExperience', '最少經驗 (年)')}</Label>
                      <Input type="number" placeholder="0" value={filterMinExperience} onChange={(e) => setFilterMinExperience(e.target.value)} className="bg-background" min="0" />
                    </div>
                    {/* Required Languages */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">{t('jobPostings.filters.requiredLanguages', '語言能力')}</Label>
                      <div className="grid grid-cols-2 gap-x-2 gap-y-2">
                        {languageDicts.map(dict => (
                          <div key={dict.code} className="flex items-center gap-2">
                            <Checkbox
                              id={`m-lang-${dict.code}`}
                              checked={filterRequiredLanguages.includes(dict.code)}
                              onCheckedChange={(checked) => {
                                if (checked) setFilterRequiredLanguages([...filterRequiredLanguages, dict.code]);
                                else setFilterRequiredLanguages(filterRequiredLanguages.filter(c => c !== dict.code));
                              }}
                            />
                            <label htmlFor={`m-lang-${dict.code}`} className="text-sm cursor-pointer">{getDictDisplayValue(dict)}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Required Skills */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">{t('jobPostings.filters.requiredSkills', '技能')}</Label>
                      <div className="grid grid-cols-2 gap-x-2 gap-y-2">
                        {[...careDicts, ...houseworkDicts].map(dict => (
                          <div key={dict.code} className="flex items-center gap-2">
                            <Checkbox
                              id={`m-skill-${dict.code}`}
                              checked={filterRequiredSkills.includes(dict.code)}
                              onCheckedChange={(checked) => {
                                if (checked) setFilterRequiredSkills([...filterRequiredSkills, dict.code]);
                                else setFilterRequiredSkills(filterRequiredSkills.filter(c => c !== dict.code));
                              }}
                            />
                            <label htmlFor={`m-skill-${dict.code}`} className="text-sm cursor-pointer">{getDictDisplayValue(dict)}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                    {activeFilterCount > 0 && (
                      <Button variant="outline" size="sm" onClick={handleResetFilters} className="w-full">
                        <X className="h-4 w-4 mr-1" />
                        {t('jobPostings.filters.clearFilters', '清除篩選')}
                      </Button>
                    )}
                  </div>
                )}


                {/* Sort & Count Bar */}
                <div className="flex items-center justify-between mb-6">
                  <span className="text-sm text-muted-foreground">
                    {t('jobPostingList.totalCount', '共 {{count}} 個職位', { count: totalElements })}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">{t('common.sortLabel', 'Sort')}</span>
                    <Select value={sortBy} onValueChange={(val) => setSortBy(val)}>
                      <SelectTrigger className="w-[160px] bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="createdAt">{t('jobPostingList.sort.createdAt', 'Recently added')}</SelectItem>
                        <SelectItem value="postedDate">{t('jobPostingList.sort.postedDate', 'Posted date')}</SelectItem>
                        <SelectItem value="minSalary">{t('jobPostingList.sort.salary', 'Salary')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <button
                      type="button"
                      onClick={() => setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC')}
                      className="p-2 rounded hover:bg-muted transition-colors"
                      title={sortDirection === 'ASC' ? t('common.sort.asc', 'Ascending') : t('common.sort.desc', 'Descending')}
                    >
                      <ArrowUpDown className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Loading State */}
                {loading && jobPostings.length === 0 ? (
                  <div className="flex justify-center py-12">
                    <Loading fullScreen={false} message={t('common.loading', '載入中...')} />
                  </div>
                ) : jobPostings.length === 0 ? (
                  /* Empty State */
                  <Card className="py-16 text-center bg-card">
                    <CardContent>
                      <div className="mx-auto w-16 h-16 bg-primary/10 text-primary rounded-full flex items-center justify-center mb-4 mt-6">
                        <FileText className="w-8 h-8" />
                      </div>
                      <h2 className="text-xl font-semibold text-foreground mb-2">{t('jobPostingList.noJobs', '暫無職位')}</h2>
                      <p className="text-muted-foreground mb-6">{t('jobPostingList.noJobsFound', '沒有找到符合條件的工作')}</p>
                    </CardContent>
                  </Card>
                ) : (
                  /* Job List */
                  <div className="space-y-4">
                    {filteredPostings.length === 0 ? (
                      <div className="py-12 text-center">
                        <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-foreground mb-2">{t('jobPostingList.noJobsFound', '沒有找到符合條件的工作')}</h3>
                        <Button variant="outline" onClick={() => setSearchKeyword('')}>
                          {t('jobPostingList.clearSearch', '清除搜尋')}
                        </Button>
                      </div>
                    ) : (
                      filteredPostings.map((posting) => (
                        <Card key={posting.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/job-postings/${posting.id}/detail`)}>
                          <CardContent className="p-6">
                            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                              <div className="flex-1 min-w-0 overflow-hidden">
                                <div className="flex items-start justify-between">
                                  <div>
                                    <RouterLink to={`/job-postings/${posting.id}/detail`} onClick={(e) => e.stopPropagation()}>
                                      <h3 className="text-xl font-semibold hover:text-primary transition-colors">
                                        {posting.headline || posting.title}
                                      </h3>
                                    </RouterLink>
                                    {(posting.employerName || posting.postedDate) && (
                                      <p className="text-muted-foreground mt-1">
                                        {posting.employerName}
                                        {posting.employerName && posting.postedDate ? ' · ' : ''}
                                        {posting.postedDate ? t('jobPostingList.postedAt', '發佈於 {{date}}', { date: formatDateLocal(posting.postedDate) }) : ''}
                                      </p>
                                    )}
                                  </div>
                                  <div className="md:hidden" onClick={(e) => e.stopPropagation()}>
                                    <BookmarkButton
                                      isBookmarked={isJobPostBookmarked(posting.id)}
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        toggleJobPostBookmark(posting);
                                        if (user?.id) {
                                          try {
                                            await apiService.statistics.recordJobPostBookmark(user.id, posting.id);
                                          } catch (error) {
                                            console.error('Error recording bookmark:', error);
                                          }
                                        }
                                      }}
                                      size="small"
                                    />
                                  </div>
                                </div>

                                <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-muted-foreground">
                                  <span className="flex items-center gap-1">
                                    <MapPin className="h-4 w-4" />
                                    {getDictValueByCode(jobLocationDicts, posting.location)}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <DollarSign className="h-4 w-4" />
                                    {formatSalary(posting)}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Clock className="h-4 w-4" />
                                    {posting.workType === 'FULL_TIME' ? t('jobPostings.filters.workTypeFull', '全職') : posting.workType === 'PART_TIME' ? t('jobPostings.filters.workTypePart', '兼職') : t('jobPostings.filters.workTypeTemp', '臨時')}
                                  </span>
                                </div>

                                <p className="text-muted-foreground mt-3 line-clamp-2 break-words">
                                  {posting.description}
                                </p>

                                <div className="flex flex-wrap gap-2 mt-4">
                                  <Badge variant="secondary" className={getStatusColor(posting.status)}>
                                    {getStatusText(posting.status)}
                                  </Badge>
                                  {posting.requiredSkills && posting.requiredSkills.slice(0, 3).map((skill, idx) => (
                                    <Badge key={idx} variant="outline">
                                      {getDictValueByCode([...houseworkDicts, ...careDicts, ...languageDicts], skill) || skill}
                                    </Badge>
                                  ))}
                                  {posting.requiredSkills && posting.requiredSkills.length > 3 && (
                                    <Badge variant="outline">+{posting.requiredSkills.length - 3}</Badge>
                                  )}
                                </div>
                              </div>

                              <div className="hidden md:flex flex-col items-end gap-3">
                                <div onClick={(e) => e.stopPropagation()}>
                                  <BookmarkButton
                                    isBookmarked={isJobPostBookmarked(posting.id)}
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      toggleJobPostBookmark(posting);
                                      if (user?.id) {
                                        try {
                                          await apiService.statistics.recordJobPostBookmark(user.id, posting.id);
                                        } catch (error) {
                                          console.error('Error recording bookmark:', error);
                                        }
                                      }
                                    }}
                                  />
                                </div>
                                <Button asChild className="bg-primary hover:bg-primary/90">
                                  <RouterLink to={`/job-postings/${posting.id}/detail`} onClick={(e) => e.stopPropagation()}>{t('jobPostingList.viewDetail', '查看詳情')}</RouterLink>
                                </Button>
                              </div>

                              <div className="md:hidden flex gap-2 w-full mt-4">
                                <Button asChild className="flex-1 bg-primary hover:bg-primary/90">
                                  <RouterLink to={`/job-postings/${posting.id}/detail`} onClick={(e) => e.stopPropagation()}>{t('jobPostingList.viewDetail', '查看詳情')}</RouterLink>
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-4 pt-6">
                    <Button variant="outline" onClick={handlePrevPage} disabled={loading || page === 0}>
                      {t('common.prevPage', '上一頁')}
                    </Button>
                    <span className="text-sm text-muted-foreground">{t('common.pageOf', '第 {{current}} / {{total}} 頁', { current: page + 1, total: totalPages })}</span>
                    <Button variant="outline" onClick={handleNextPage} disabled={loading || !hasMore}>
                      {t('common.nextPage', '下一頁')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default JobPostingListPage;
